import { useSortable } from "@dnd-kit/sortable";
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowForward from "@mui/icons-material/ArrowForward";
import {
  ActivePlayerOnFloorTable,
  ActiveUnHostedPlayerOnFloorTable,
  GuestAndHostAuditTable,
  HostTripTable,
  ReservationTable,
  TrendingCustomerScoresTable,
  UpcomingPlayerBirthdayTable,
} from "../Tables";
import Routes from "constants/routes";
import { HostSummaryByTier } from "../SummaryByTier";
import { HostEventsTable } from "../Tables/Events/HostEventsTable";
import { HostPromotionsTable } from "../Tables/Promotions/HostPromotionsTable";
import { useNavigate } from "react-router-dom";
import {
  Assessment,
  AutoGraph,
  Cake,
  CalendarToday,
  Campaign,
  Event,
  EventAvailable,
  LocationOn,
  TrendingDown,
  TrendingUp,
  VerifiedUser,
  Whatshot,
  Warning,
  Star,
} from "@mui/icons-material";
import { AppPermissions } from "constants/permissions";
import { Permission } from "@sanmanuel/permissions";
import { useMobile } from "hooks/Mobile/useMobile";
import { BuildUpPlayersOnFloorTable } from "../Tables/BuildUpPlayersOnFloorTable";
import { PlayersOfInterestTable } from "../Tables/PlayersOfInterestTable";

interface IDashboardWidgetCardProps {
  avatar?: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  onDetails?: () => void;
  children?: ReactNode | ReactNode[];
  overlay?: ReactNode;
}

export const DashboardWidgetCard = (props: IDashboardWidgetCardProps): JSX.Element => {
  const { title, action, avatar, onDetails, children, overlay } = props;
  const isMobile = useMobile();
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        position: "relative",
      }}
    >
      <CardHeader avatar={avatar} title={title} titleTypographyProps={{ variant: "h6" }} action={action} />
      <CardContent sx={{ flex: 1 }}>{children}</CardContent>
      {onDetails && (
        <CardActions
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
          }}
        >
          <Button variant="contained" endIcon={<ArrowForward />} fullWidth={isMobile} onClick={onDetails}>
            View All
          </Button>
        </CardActions>
      )}
      {overlay}
    </Card>
  );
};

export type DashboardWidgetKey =
  | "active-unhosted-players-on-floor"
  | "active-players-on-floor"
  | "active-players-summary"
  | "trips-increasing"
  | "trips-decreasing"
  | "events"
  | "promotions"
  | "active-reservations"
  | "upcoming-reservations"
  | "host-audit"
  | "player-birthdays"
  | "trending-customer-value"
  | "build-up-players-on-floor"
  | "players-of-interest";

export type DashboardWidgetInfo = {
  icon?: ReactNode;
  title: ReactNode;
  detailsRoute?: string;
  body: ReactNode;
  permissionsRequired: Permission | Permission[];
};

const dashboardGridProps = {
  pagination: true,
  paginationModel: { page: 0, pageSize: 5 },
  hideFooter: true,
};

export const dashboardWidgets: Record<DashboardWidgetKey, DashboardWidgetInfo> = {
  "players-of-interest": {
    icon: <Star />,
    title: "Players of Interest",
    body: <PlayersOfInterestTable {...dashboardGridProps} />,
    detailsRoute: Routes.playersOfInterest,
    permissionsRequired: [],
  },
  "active-unhosted-players-on-floor": {
    icon: <Whatshot />,
    title: "Hot Players on Floor (Non-Hosted)",
    body: <ActiveUnHostedPlayerOnFloorTable {...dashboardGridProps} />,
    detailsRoute: Routes.activeUnHostedPlayersOnFloor,
    permissionsRequired: [AppPermissions.view.activePlayers],
  },
  "active-players-on-floor": {
    icon: <LocationOn />,
    title: "My Active Players on Floor",
    body: <ActivePlayerOnFloorTable {...dashboardGridProps} />,
    detailsRoute: Routes.activePlayersOnFloor,
    permissionsRequired: [AppPermissions.view.activePlayers],
  },
  "active-players-summary": {
    icon: <Assessment />,
    title: "My Active Players - Summary By Tier",
    body: <HostSummaryByTier />,
    permissionsRequired: [AppPermissions.view.activePlayers],
  },
  "trips-increasing": {
    icon: <TrendingUp />,
    title: "Trips Increasing",
    body: <HostTripTable type="increasing" {...dashboardGridProps} />,
    detailsRoute: Routes.tripsIncreasing,
    permissionsRequired: [AppPermissions.view.trips],
  },
  "trips-decreasing": {
    icon: <TrendingDown />,
    title: "Trips Decreasing",
    body: <HostTripTable type="decreasing" {...dashboardGridProps} />,
    detailsRoute: Routes.tripsDecreasing,
    permissionsRequired: [AppPermissions.view.trips],
  },
  events: {
    icon: <Event />,
    title: "Upcoming Events",
    body: <HostEventsTable {...dashboardGridProps} from="dashboard" />,
    detailsRoute: Routes.hostEvents,
    permissionsRequired: [AppPermissions.view.events],
  },
  promotions: {
    icon: <Campaign />,
    title: "Upcoming Promotions",
    body: <HostPromotionsTable {...dashboardGridProps} from="dashboard" />,
    detailsRoute: Routes.hostPromotions,
    permissionsRequired: [AppPermissions.view.promotions],
  },
  "active-reservations": {
    icon: <EventAvailable />,
    title: "Active Reservations",
    body: <ReservationTable status={"active"} {...dashboardGridProps} />,
    detailsRoute: Routes.activeReservations,
    permissionsRequired: [AppPermissions.view.hotel],
  },
  "upcoming-reservations": {
    icon: <CalendarToday />,
    title: "Upcoming Reservations",
    body: <ReservationTable status={"upcoming"} {...dashboardGridProps} />,
    detailsRoute: Routes.upcomingReservations,
    permissionsRequired: [AppPermissions.view.hotel],
  },
  "host-audit": {
    icon: <VerifiedUser />,
    title: "Guest-and-Host Audit",
    body: <GuestAndHostAuditTable {...dashboardGridProps} />,
    detailsRoute: Routes.guestAndHostAudit,
    permissionsRequired: [AppPermissions.view.playerHostAudit],
  },
  "player-birthdays": {
    icon: <Cake />,
    title: "My Players Upcoming Birthday",
    body: <UpcomingPlayerBirthdayTable {...dashboardGridProps} />,
    detailsRoute: Routes.birthdays,
    permissionsRequired: [AppPermissions.view.playerInfo],
  },
  "trending-customer-value": {
    icon: <AutoGraph />,
    title: "Trending Customer Value",
    body: <TrendingCustomerScoresTable {...dashboardGridProps} />,
    detailsRoute: Routes.trendingCustomerValues,
    permissionsRequired: [AppPermissions.view.playerStats],
  },
  "build-up-players-on-floor": {
    icon: <Warning />,
    title: "Flagged Free Play", // NOTE: name can change so just changing the title for now
    body: <BuildUpPlayersOnFloorTable {...dashboardGridProps} />,
    detailsRoute: Routes.viewBuildUpPlayers,
    permissionsRequired: [AppPermissions.view.antiFraud],
  },
};

export const DashboardPermissions = Object.values(dashboardWidgets).flatMap((dw) => dw.permissionsRequired);

export interface IDashboardWidgetProps {
  id: DashboardWidgetKey;
  size: number;
  action?: ReactNode;
  overlay?: ReactNode;
}

export const DashboardWidget = ({ id, size, action, overlay }: IDashboardWidgetProps): JSX.Element => {
  const navigate = useNavigate();
  const dashboardWidgetInfo = dashboardWidgets[id];
  const route = dashboardWidgetInfo.detailsRoute;
  return (
    <DashboardWidgetCard
      avatar={dashboardWidgetInfo.icon}
      title={dashboardWidgetInfo.title}
      action={action}
      onDetails={route ? () => navigate(route) : undefined}
      overlay={overlay}
    >
      {dashboardWidgetInfo.body}
    </DashboardWidgetCard>
  );
};

export const SortableDashboardWidget = ({ id, md }: { id: DashboardWidgetKey; md: number }): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const theme = useTheme();
  const isVeryLargeScreen = useMediaQuery(theme.breakpoints.only("xl"));
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : "none",
    transition,
    opacity: isDragging ? 0.1 : 1,
  };

  return (
    <Grid
      item
      style={style}
      ref={setNodeRef}
      xs={12}
      md={isVeryLargeScreen ? md / 2 : md}
      justifyContent="center"
      alignItems="center"
    >
      <DashboardWidget
        id={id}
        size={md}
        action={<DragIndicatorIcon sx={{ cursor: "grab" }} {...attributes} {...listeners} />}
        overlay={
          isDragging ? (
            <Paper
              sx={{
                bgcolor: "text.primary",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 10000,
              }}
            />
          ) : undefined
        }
      />
    </Grid>
  );
};
